import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, Info, Play, ScrollText } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk } from "@/lib/queryClient";
import { LogsSkeleton } from "@/components/PageSkeletons";

const LEVELS = [
  { value: "", label: "All levels" },
  { value: "info", label: "Info" },
  { value: "warn", label: "Warn" },
  { value: "error", label: "Error" },
];

function LevelDot({ level }) {
  const color =
    level === "error"
      ? "bg-hm-danger"
      : level === "warn"
        ? "bg-hm-warn"
        : "bg-hm-accent-dim";
  return <span className={"h-1.5 w-1.5 rounded-full " + color} />;
}

function LogRow({ row }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`log-row-${row.id}`}
      className="hm-surface mono text-[11px]"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-3 py-2 text-left"
      >
        <LevelDot level={row.level} />
        <span className="text-hm-fg-subtle w-[80px] truncate">
          {(row.ts || "").slice(11, 19)}
        </span>
        <span className="w-[120px] text-hm-accent truncate">{row.agent || "—"}</span>
        <span className="flex-1 min-w-0 truncate text-hm-fg-muted">{row.message}</span>
        {row.latency_ms != null && (
          <span className="text-hm-fg-subtle">{row.latency_ms}ms</span>
        )}
        <ChevronDown
          className={
            "h-3.5 w-3.5 text-hm-fg-subtle transition-transform " +
            (open ? "rotate-180" : "")
          }
        />
      </button>
      {open && (
        <div className="border-t border-hm-border p-3 space-y-1">
          <div className="text-hm-fg-subtle">
            trace: <span className="text-hm-fg-muted">{row.trace_id || "—"}</span>
          </div>
          <div className="text-hm-fg-subtle">
            run: <span className="text-hm-fg-muted">{row.run_id || "—"}</span>
          </div>
          {row.payload && Object.keys(row.payload).length > 0 && (
            <pre className="whitespace-pre-wrap break-words text-hm-fg-muted max-h-64 overflow-y-auto pt-2">
              {typeof row.payload === "string" ? row.payload : JSON.stringify(row.payload, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export default function LogsPage() {
  const [agent, setAgent] = useState("");
  const [level, setLevel] = useState("");
  const [traceId, setTraceId] = useState("");
  const [follow, setFollow] = useState(false);
  const streamRef = useRef(null);
  const queryClient = useQueryClient();

  const params = { agent, level, trace_id: traceId, limit: 150 };
  const { data: rows, isPending, refetch } = useQuery({
    queryKey: qk.logs(params),
    queryFn: () => {
      const p = new URLSearchParams();
      if (agent) p.set("agent", agent);
      if (level) p.set("level", level);
      if (traceId) p.set("trace_id", traceId);
      p.set("limit", "150");
      return fetchers.logs({ ...(agent ? { agent } : {}), ...(level ? { level } : {}), limit: 150 })
        .then((r) => r); // fetcher already returns parsed json
    },
  });

  const fetchLogs = useCallback(() => refetch(), [refetch]);

  useEffect(() => {
    if (!follow) return;
    const controller = new AbortController();
    streamRef.current = controller;
    (async () => {
      try {
        const res = await fetch(`${API}/events/stream`, {
          headers: authHeaders(),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let sep;
          while ((sep = buf.indexOf("\n\n")) !== -1) {
            const raw = buf.slice(0, sep);
            buf = buf.slice(sep + 2);
            if (raw.includes("event: activity")) {
              // an activity fired — refetch a small batch
              fetchLogs();
            }
          }
        }
      } catch {
        /* aborted */
      }
    })();
    return () => controller.abort();
  }, [follow, fetchLogs]);

  return (
    <div data-testid="logs-page">
      <PageHeader
        title="Logs"
        badge="Live"
        subtitle="Every request, every agent step, every LLM call. Structured, filterable."
        actions={
          <button
            data-testid="logs-follow-toggle"
            onClick={() => setFollow((f) => !f)}
            className={
              "flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors " +
              (follow
                ? "bg-hm-accent text-hm-bg border-hm-accent"
                : "border-hm-border text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong")
            }
          >
            <Play className="h-3.5 w-3.5" />
            {follow ? "Following live" : "Follow live"}
          </button>
        }
      />

      <div className="hm-surface p-3 mb-4 flex flex-wrap items-center gap-3">
        <input
          data-testid="logs-filter-agent"
          value={agent}
          onChange={(e) => setAgent(e.target.value)}
          placeholder="agent name (e.g. news_scanner)"
          className="h-8 px-3 rounded-md bg-hm-bg-1 border border-hm-border text-xs text-hm-fg placeholder:text-hm-fg-subtle focus:outline-none focus:border-hm-accent-dim min-w-[200px]"
        />
        <select
          data-testid="logs-filter-level"
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="h-8 px-2 rounded-md bg-hm-bg-1 border border-hm-border text-xs text-hm-fg focus:outline-none focus:border-hm-accent-dim"
        >
          {LEVELS.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
        <input
          data-testid="logs-filter-trace"
          value={traceId}
          onChange={(e) => setTraceId(e.target.value)}
          placeholder="trace_id / run_id"
          className="h-8 px-3 rounded-md bg-hm-bg-1 border border-hm-border text-xs text-hm-fg placeholder:text-hm-fg-subtle focus:outline-none focus:border-hm-accent-dim min-w-[220px]"
        />
        <button
          onClick={fetchLogs}
          className="ml-auto h-8 px-3 rounded-md border border-hm-border text-xs text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong"
        >
          Refresh
        </button>
      </div>

      {isPending || !rows ? (
        <LogsSkeleton rows={12} />
      ) : rows.length === 0 ? (
        <div className="hm-surface p-8 text-center">
          <ScrollText className="h-6 w-6 text-hm-fg-subtle mx-auto mb-2" />
          <div className="text-sm text-hm-fg-muted">
            No logs match your filters. Trigger a chat to see agent activity here.
          </div>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="logs-list">
          {rows.map((r) => (
            <LogRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}
